import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPluviometerStation,
  upsertPluviometerDailyReading,
} from "../../utils/farmPluviometryApi";
import { toDateInputValue } from "../../utils/rainHeatmapUtils";

function formatCoord(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return String(Math.round(number * 1e6) / 1e6);
}

function PluviometerCreateModal({
  farmId,
  onClose,
  onCreated,
  initialLatitude,
  initialLongitude,
  pickedFromMap,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [stationNumber, setStationNumber] = useState("");
  const [latitude, setLatitude] = useState(formatCoord(initialLatitude));
  const [longitude, setLongitude] = useState(formatCoord(initialLongitude));
  const [rainMm, setRainMm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLatitude(formatCoord(initialLatitude));
    setLongitude(formatCoord(initialLongitude));
  }, [initialLatitude, initialLongitude]);

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");

    const lat = Number(String(latitude).replace(",", "."));
    const lng = Number(String(longitude).replace(",", "."));
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      setError(t("Informe latitude e longitude válidas."));
      setSaving(false);
      return;
    }

    try {
      const station = await createPluviometerStation(farmId, {
        name: name.trim(),
        stationNumber: stationNumber.trim(),
        latitude: lat,
        longitude: lng,
      });
      const mm = Number(String(rainMm).replace(",", "."));
      if (station && station.uuid && rainMm !== "" && Number.isFinite(mm)) {
        await upsertPluviometerDailyReading(farmId, station.uuid, {
          date: toDateInputValue(),
          rainMm: mm,
        });
      }
      onCreated(station);
    } catch (submitError) {
      const detail =
        submitError &&
        submitError.response &&
        submitError.response.data &&
        submitError.response.data.detail;
      setError(
        detail ||
          t(
            "Não foi possível cadastrar o pluviômetro. Verifique a distância mínima (60 m) e tente novamente."
          )
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="farm-pluviometer-modal">
      <button
        type="button"
        className="farm-pluviometer-modal__backdrop"
        aria-label={t("Fechar")}
        onClick={onClose}
      />
      <form className="farm-pluviometer-modal__panel" onSubmit={handleSubmit}>
        <h3>{t("Cadastrar pluviômetro")}</h3>
        <p className="farm-pluviometer-modal__hint">
          {pickedFromMap
            ? t(
                "Ponto marcado no mapa. Confira o nome e, se quiser simular o calor, informe os mm de hoje."
              )
            : t(
                "Cadastre o ponto na plataforma. Mantenha pelo menos 100 m entre pluviômetros (o sistema reconhece o mesmo ponto em até 60 m)."
              )}
        </p>
        <label>
          {t("Nome")}
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("Pluviômetro 3")}
          />
        </label>
        <label>
          {t("Número")}
          <input
            type="text"
            value={stationNumber}
            onChange={(event) => setStationNumber(event.target.value)}
            placeholder="3"
          />
        </label>
        <label>
          {t("Latitude")}
          <input
            type="number"
            step="any"
            value={latitude}
            onChange={(event) => setLatitude(event.target.value)}
            required
          />
        </label>
        <label>
          {t("Longitude")}
          <input
            type="number"
            step="any"
            value={longitude}
            onChange={(event) => setLongitude(event.target.value)}
            required
          />
        </label>
        <label>
          {t("Chuva de hoje (mm), opcional")}
          <input
            type="number"
            step="any"
            min="0"
            value={rainMm}
            onChange={(event) => setRainMm(event.target.value)}
            placeholder="0"
          />
        </label>
        {error ? <div className="farm-pluviometer-modal__error">{error}</div> : null}
        <div className="farm-pluviometer-modal__actions">
          <button type="button" className="btn btn-default" onClick={onClose}>
            {t("Cancelar")}
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? t("Salvando...") : t("Cadastrar")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PluviometerCreateModal;
